import { useEffect, useState, type FormEvent } from 'react'
import { coursesAPI } from '../../services/api'
import type { QuizView } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'
import { courseProblems } from './courseUtils'

const refusalText = (err: unknown, fallback: string) => {
  const problems = courseProblems(err)
  return problems ? problems.map((p) => `${p.where}: ${p.message}`).join(' ') : apiErrorMessage(err, fallback)
}

/**
 * A quiz lesson's questions, for its instructor: multiple choice, with the
 * right option and an optional explanation students see after submitting.
 * The pass mark and attempts are set here too. Once the course is live,
 * questions can be added but not removed (students were marked on them).
 */
const QuizEditor = ({ quizId, live, onClose }: { quizId: number; live: boolean; onClose: () => void }) => {
  const [quiz, setQuiz] = useState<QuizView | null>(null)
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [correct, setCorrect] = useState(0)
  const [explanation, setExplanation] = useState('')
  const [passMark, setPassMark] = useState('')
  const [attempts, setAttempts] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = () =>
    coursesAPI
      .quiz(quizId)
      .then((q) => {
        setQuiz(q)
        setPassMark(String(q.passing_score))
        setAttempts(String(q.max_attempts))
      })
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load the quiz.")))
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId])

  const filled = options.map((o) => o.trim()).filter(Boolean)
  const add = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await coursesAPI.addQuizQuestion(quizId, {
        question_text: question.trim(),
        options: options.map((o) => o.trim()),
        correct_option: correct,
        explanation: explanation.trim() || null,
      })
      setQuestion('')
      setOptions(['', ''])
      setCorrect(0)
      setExplanation('')
      await load()
    } catch (err) {
      setError(refusalText(err, "Couldn't add the question."))
    }
  }

  const saveSettings = async () => {
    setError(null)
    try {
      setQuiz(await coursesAPI.updateQuiz(quizId, { passing_score: Number(passMark), max_attempts: Number(attempts) }))
    } catch (err) {
      setError(refusalText(err, "Couldn't save the quiz."))
    }
  }

  const remove = async (id: number) => {
    setError(null)
    try {
      await coursesAPI.deleteQuizQuestion(id)
      await load()
    } catch (err) {
      setError(refusalText(err, "Couldn't remove the question."))
    }
  }

  if (!quiz) return error ? <p className="text-sm text-danger-700">{error}</p> : null

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm" data-testid="quiz-editor">
      <div className="flex flex-wrap items-end gap-3">
        <label>
          <span className="block text-xs text-gray-600">Pass mark (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            value={passMark}
            onChange={(e) => setPassMark(e.target.value)}
            className="w-20 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <label>
          <span className="block text-xs text-gray-600">Attempts (0: unlimited)</span>
          <input
            type="number"
            min={0}
            max={20}
            value={attempts}
            onChange={(e) => setAttempts(e.target.value)}
            className="w-20 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <button type="button" onClick={saveSettings} className="rounded-md border border-gray-300 px-3 py-1 text-gray-800">
          Save
        </button>
        <button type="button" onClick={onClose} className="ml-auto text-gray-600">
          Done
        </button>
      </div>

      <ol className="space-y-2" data-testid="quiz-questions">
        {quiz.questions.map((q, n) => (
          <li key={q.id} className="rounded border border-gray-200 bg-white p-2">
            <p className="flex gap-2">
              <span className="font-medium text-gray-900">
                {n + 1}. {q.question_text}
              </span>
              {!live && (
                <button type="button" onClick={() => remove(q.id)} className="ml-auto text-xs text-danger-700">
                  Remove
                </button>
              )}
            </p>
            <ul className="mt-1 pl-4 text-gray-700">
              {q.options.map((o, i) => (
                <li key={i} className={i === q.correct_option ? 'font-medium text-success-700' : ''}>
                  {i === q.correct_option ? '✓ ' : ''}
                  {o}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      <form onSubmit={add} className="space-y-2" aria-label="New question">
        <input
          aria-label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={1000}
          placeholder="Question"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5"
        />
        {options.map((option, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="radio"
              name="correct"
              aria-label={`Option ${i + 1} is right`}
              checked={correct === i}
              onChange={() => setCorrect(i)}
            />
            <input
              aria-label={`Option ${i + 1}`}
              value={option}
              onChange={(e) => setOptions((list) => list.map((o, j) => (j === i ? e.target.value : o)))}
              maxLength={300}
              placeholder={`Option ${i + 1}`}
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1"
            />
          </div>
        ))}
        {options.length < 6 && (
          <button type="button" onClick={() => setOptions((list) => [...list, ''])} className="text-xs text-primary-700">
            + Another option
          </button>
        )}
        <input
          aria-label="Explanation"
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          maxLength={1000}
          placeholder="Why that's the answer (shown after submitting)"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5"
        />
        <button
          type="submit"
          disabled={!question.trim() || filled.length !== options.length || options.length < 2}
          className="rounded-md bg-primary-600 px-3 py-1.5 font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Add question
        </button>
      </form>
      {error && (
        <p role="alert" className="text-danger-700">
          {error}
        </p>
      )}
    </div>
  )
}

export default QuizEditor
